/**
 * useLeadCaptureTrigger — decide when to open the modal on mobile/desktop.
 *
 * Rules aligned with the Master Directive:
 *  - Never auto-open immediately on landing.
 *  - Fire on the FIRST of: 45s active browsing OR 60% scroll OR 2 products viewed.
 *  - Do not display during checkout, cart drawer, image viewer or another modal.
 *  - After dismissal: 7-day cooldown.
 *  - After successful submission: never again.
 *  - Respect prefers-reduced-motion (skip auto-trigger).
 */
import { useEffect, useState } from "react";

const STORAGE_KEYS = {
  submitted: "kelo_lead_submitted",
  dismissedAt: "kelo_lead_dismissed_at",
};
const COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function inBlockingContext() {
  if (typeof document === "undefined") return true;
  // If a shadcn dialog / cart drawer is open, body has data-scroll-locked or a drawer test id is visible.
  if (document.body.dataset.scrollLocked === "1") return true;
  if (document.querySelector("[data-testid=cart-drawer]")) return true;
  if (document.querySelector("[data-lead-modal-open=\"1\"]")) return true;
  return false;
}

export function markLeadSubmitted() {
  try {
    localStorage.setItem(STORAGE_KEYS.submitted, "1");
  } catch (_e) {
    /* storage disabled */
  }
}

export function markLeadDismissed() {
  try {
    localStorage.setItem(STORAGE_KEYS.dismissedAt, String(Date.now()));
  } catch (_e) {
    /* storage disabled */
  }
}

export function useLeadCaptureTrigger({ enabled = true } = {}) {
  const [shouldOpen, setShouldOpen] = useState(false);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") return;

    // Already submitted? Never open again.
    try {
      if (localStorage.getItem(STORAGE_KEYS.submitted) === "1") return;
      const dismissedAt = Number(localStorage.getItem(STORAGE_KEYS.dismissedAt) || 0);
      if (dismissedAt && Date.now() - dismissedAt < COOLDOWN_MS) return;
    } catch (_e) {
      /* storage disabled — proceed */
    }

    let armed = true;
    let productsViewed = 0;

    const open = () => {
      if (!armed) return;
      if (inBlockingContext()) return;
      armed = false;
      setShouldOpen(true);
    };

    // Trigger 1: time-based (45s of active browsing)
    let elapsed = 0;
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        elapsed += 1000;
        if (elapsed >= 45_000) {
          window.clearInterval(timer);
          open();
        }
      }
    }, 1000);

    // Trigger 2: scroll depth (60%)
    const onScroll = () => {
      const doc = document.documentElement;
      const scrolled =
        (doc.scrollTop || document.body.scrollTop) /
        Math.max(1, doc.scrollHeight - doc.clientHeight);
      if (scrolled >= 0.6) {
        window.removeEventListener("scroll", onScroll);
        open();
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    // Trigger 3: 2+ product cards observed
    const productCards = document.querySelectorAll("[data-testid^=collection-card-],[data-testid=flagship-image]");
    let io;
    if (productCards.length && "IntersectionObserver" in window) {
      io = new IntersectionObserver((entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            productsViewed += 1;
            io.unobserve(e.target);
            if (productsViewed >= 2) {
              io.disconnect();
              open();
              break;
            }
          }
        }
      }, { threshold: 0.6 });
      productCards.forEach((el) => io.observe(el));
    }

    return () => {
      armed = false;
      window.clearInterval(timer);
      window.removeEventListener("scroll", onScroll);
      if (io) io.disconnect();
    };
  }, [enabled]);

  return { shouldOpen, setShouldOpen };
}
